/**
 * **CustomSocialAuth** React page: custom federated login **callback** (non–Hosted UI).
 *
 * Routed at **`/social-auth`**.
 *
 * **`?popup=true`:** persists a session flag across the OAuth round-trip when the redirect URI cannot carry
 * query params. **Popup:** progress **`{@link CustomSocialAuthStateCookie}`** cookie reads/writes, terminal
 * **`window.close()`**. **Root (no `popup`):** no progress cookie; success uses cached **`redirect_url`**
 * (same as login pages), errors **`/login?…`**.
 *
 * Do not confuse with {@link ../SocialLogin/SocialLogin} (`/social-login`), which exchanges Hosted UI
 * `code` / `state`.
 */
import React, { useEffect, useState } from 'react';
import { Spinner as BootstrapSpinner } from 'react-bootstrap';
import { logging } from '../../SharedLib/Util/LoggingUtil';
import { clearProcessUserSessionDethrottleCache } from '../../SharedLib/Util/CognitoUtil';
import type { IUserSessionLifespan } from '../../SharedLib/Util/Interfaces';
import {
  CUSTOM_SOCIAL_AUTH_OAUTH_CODE_VERIFIER_STORAGE_KEY,
  CUSTOM_SOCIAL_AUTH_OAUTH_STATE_STORAGE_KEY,
  advanceCustomSocialAuthStateCookie,
  cacheCustomSocialAuthRedirectUrl,
  finishCustomSocialAuthFullPageRedirect,
  getStoredCustomSocialState,
  getStoredCustomSocialVerifier,
  parseCustomSocialAuthInitProvider,
  handleError,
  resetCustomSocialAuthStateCookieForNewInit,
  startCustomSocialAuthorizeRedirect,
  syncCustomSocialAuthPopupModeFromQuery,
  upsertSessionCookiesAfterConsume,
} from './CustomSocialAuthUtil';
import LoadingBox from '../../SharedLib/Component/LoadingBox/LoadingBox';
import styles from '../SocialLogin/SocialLogin.module.scss';
import SocialSpinner from '../SocialLogin/Spinner/Spinner';
import AcademyBanner from '../AcademyBanner/AcademyBanner';
import LoginLayout from '../LoginLayout/LoginLayout';
import loginFullStyles from '../LoginFullPageNew/LoginFullPage.module.scss';
import newLoginStyles from '../NewLogin/NewLoginPage.module.scss';

const CustomSocialAuth: React.FC = () => {
  const [isPopupFlow] = useState(() =>
    syncCustomSocialAuthPopupModeFromQuery(new URLSearchParams(window.location.search)),
  );

  useEffect(() => {
    (window as any).zE?.('webWidget', 'hide');

    const completeLogin = async () => {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        const isPopup = syncCustomSocialAuthPopupModeFromQuery(queryParams);

        const isCustomSocialAuthInitBootstrap =
          queryParams.get('init') === 'true'
          && !queryParams.get('consume_token')
          && !queryParams.get('error');

        if (isCustomSocialAuthInitBootstrap) {
          const providerNorm = parseCustomSocialAuthInitProvider(queryParams.get('provider'));
          if (!providerNorm) {
            logging.logError('Social auth init: missing or unknown provider', {
              providerRaw: queryParams.get('provider'),
            });
            handleError({
              code: 'INVPSINI',
              description:
                'Invalid sign-in provider. Please start again from the app you signed in from.',
            });
            return;
          }
          cacheCustomSocialAuthRedirectUrl();
          if (isPopup) {
            resetCustomSocialAuthStateCookieForNewInit(providerNorm);
          }
          void startCustomSocialAuthorizeRedirect(providerNorm);
          return;
        }

        if (Array.from(queryParams.keys()).length === 0) {
          logging.logError('Social auth opened with no query parameters');
          handleError({
            code: 'NILSAUTHQ',
            description: 'Failed to initialize login. Please try again.',
          });
          return;
        }

        const error = queryParams.get('error');
        const errorDescription = queryParams.get('error_description');
        const returnedState = queryParams.get('state');
        const expectedState = getStoredCustomSocialState();

        if (error) {
          logging.logError('Social auth returned error in URL', {
            error,
            errorDescription,
          });

          if (error === 'invalid_state' || error === 'state_mismatch') {
            handleError({
              code: 'CLNSTOMIS',
              description:
                errorDescription
                ?? 'We could not verify your login request (state mismatch). Please start the login process again from the original tab.',
            });
          } else if (errorDescription) {
            handleError({
              code: 'IDPOCBGEN',
              description: errorDescription,
            });
          } else {
            handleError({
              code: 'IDPOCBGEN',
              description: 'Failed to complete login. Please try again.',
            });
          }
          return;
        }

        if (expectedState && returnedState !== expectedState) {
          logging.logError('Social auth state mismatch', {
            expectedState,
            returnedState,
          });

          handleError({
            code: 'CLNSTOMIS',
            description:
              'We could not verify your login request (state mismatch). Please start the login process again from the original tab.',
          });
          return;
        }

        const consumeToken = queryParams.get('consume_token');

        if (!consumeToken) {
          handleError({
            code: 'MISNSTOK',
            description: 'Missing consume_token in URL. Please try logging in again.',
          });
          return;
        }

        if (isPopup) {
          advanceCustomSocialAuthStateCookie({
            step: 'IDP_CALLBACK',
            description: 'Returned from identity provider with consume_token',
            percentage: 40,
          });
        }

        const currentUiUts = Math.floor(Date.now() / 1000);
        const codeVerifier = getStoredCustomSocialVerifier();

        const response = await fetch('/api/user/session', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            method: 'GET',
            consumeToken,
            currentUiUts,
            ...(codeVerifier ? { codeVerifier } : {}),
          }),
        });

        const responseText = await response.text();

        type IConsumeBody = {
          error?: { code: string; description?: string };
          data?: {
            sessionId: string;
            accessToken: string;
            sessionLifespan: IUserSessionLifespan;
            userInfoCookie: string;
          };
        };

        let consumeBody: IConsumeBody;
        try {
          consumeBody = responseText ? JSON.parse(responseText) : {};
        } catch (parseErr) {
          logging.logError('Social auth consume response parse failed', {
            parseErr,
            httpStatus: response.status,
            bodySnippet: responseText.slice(0, 500),
          });
          handleError({
            code: 'SESCONPAR',
            description: 'Failed to complete login. Please try again.',
          });
          return;
        }

        if (consumeBody.error?.code !== '0') {
          logging.logError('Social auth consume failed (application error)', {
            httpStatus: response.status,
            body: consumeBody,
          });
          const desc =
            consumeBody.error?.description?.trim() ?? '';
          let message =
            desc || 'Failed to complete login. Please try again.';
          if (consumeBody.error?.code === 'CTHABU') {
            message =
              'This sign-in link was already used or is no longer valid. Please start sign-in again from the login page.';
          }
          const upstreamCodeRaw = consumeBody.error?.code?.trim() ?? '';
          const usableUpstreamCode =
            upstreamCodeRaw !== '' && upstreamCodeRaw !== '0' && upstreamCodeRaw !== '!';

          handleError({
            code: usableUpstreamCode ? upstreamCodeRaw : 'SESCONAFL',
            description: message,
          });
          return;
        }

        if (
          !response.ok
          || !consumeBody.data?.accessToken
          || !consumeBody.data.sessionLifespan
          || !consumeBody.data.userInfoCookie
          || !consumeBody.data.sessionId
        ) {
          logging.logError('Social auth consume invalid response', {
            httpStatus: response.status,
            ok: response.ok,
            body: consumeBody,
          });
          handleError({
            code: 'SESCOINVS',
            description: 'Failed to complete login. Please try again.',
          });
          return;
        }

        if (isPopup) {
          advanceCustomSocialAuthStateCookie({
            step: 'SESSION_CONSUME_OK',
            description: 'Backend session consume succeeded',
            percentage: 85,
          });
        }

        const upsertRes = upsertSessionCookiesAfterConsume({
          accessToken: consumeBody.data.accessToken,
          userInfoCookie: consumeBody.data.userInfoCookie,
          sessionId: consumeBody.data.sessionId,
          sessionLifespan: consumeBody.data.sessionLifespan,
        });

        if (upsertRes.error.code !== '0') {
          logging.logError('Social auth upsert cookies failed', upsertRes);
          handleError({
            code: 'SESSCKUPS',
            description: 'Failed to complete login. Please try again.',
          });
          return;
        }

        if (isPopup) {
          advanceCustomSocialAuthStateCookie({
            step: 'COMPLETED',
            description: 'Session cookies installed',
            percentage: 100,
            completed: true,
          });
        }

        clearProcessUserSessionDethrottleCache();

        const isNewUser = queryParams.get('is_new_user') === 'true';

        const url = new URL(window.location.href);
        url.searchParams.delete('consume_token');
        url.searchParams.delete('is_new_user');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.toString());

        sessionStorage.removeItem(CUSTOM_SOCIAL_AUTH_OAUTH_STATE_STORAGE_KEY);
        sessionStorage.removeItem(CUSTOM_SOCIAL_AUTH_OAUTH_CODE_VERIFIER_STORAGE_KEY);

        if (isPopup) {
          window.close();
          return;
        }

        await finishCustomSocialAuthFullPageRedirect({ isNewUser });
      } catch (err) {
        logging.logError('Social auth error:', err);
        handleError({
          code: 'UNEXLOGIN',
          description: 'An unexpected error occurred during login',
        });
      }
    };

    completeLogin();
  }, []);

  if (isPopupFlow) {
    return (
      <div className={styles.social_login}>
        <div className={styles.modal_inner}>
          <LoadingBox
            position="fixed"
            top_displacement="auto"
            text="Just a moment, signing you in ..."
            textSize="20px"
            textWeight="normal"
            spinner={<SocialSpinner />}
            text_placement="bottom"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <AcademyBanner />
      <div className={loginFullStyles.loginWrapper}>
        <LoginLayout>
          <div className={newLoginStyles.loader_container}>
            <BootstrapSpinner
              animation="border"
              role="status"
              variant="white"
              className="loader-spinner"
            >
              <span className="visually-hidden">Loading...</span>
            </BootstrapSpinner>
          </div>
        </LoginLayout>
      </div>
    </>
  );
};

export default CustomSocialAuth;
